import Socket from './socket/socket'
import ajax from 'aria-viewer/utils/ajax'
import { getEnv } from 'aria-viewer/utils/environment'
import { helper, events, GpsCollector } from 'aria-viewer/legacy/'
import Cookie from 'aria-viewer/utils/cookie'
import { _btoa } from './analyticUtils'
import qs from 'qs'

class Analytics {
    static analyticsData = {
        userkey: null,
        sessionId: null,
        module: null,
        moduleId: null,
        socketUrl: null,
        socket: null,
        gpsCollector: null,
        queuedStop: null,
        inst_id: null,
        loc_id: null,
        createAnalyticsModuleParams: {},
        hasInitialized: false,
    }

    static init({ inst_id, loc_id, socket, analyticsUrl, autoload = true }) {
        // Determine if a socket connection already exists. If so close it.
        Analytics.closeSocket()

        Analytics.analyticsData.inst_id = inst_id
        Analytics.analyticsData.loc_id = loc_id

        helper.bind(events.stopChanged, Analytics._stopEntered)
        helper.bind(events.sessionIdChanged, Analytics._sessionIdChanged)
        window.addEventListener('message', Analytics._onChildIframeAnalyticsEvent, false)

        if (autoload) {
            return Analytics.getAnalyticsModule(inst_id, loc_id).then(({ data }) => {
                if (data && data.analytics) {
                    const loadConfig = {
                        ...data.analytics,
                        socket: socket ? socket : data.analytics.socket,
                        analyticsUrl: analyticsUrl ? analyticsUrl : data.analytics.analyticsUrl,
                    }
                    Analytics.load(loadConfig)
                    return loadConfig
                } else {
                    // in development environments, there seems to be a problem with analytics data
                    console.error('Error loading analytics data. Possibly pointing to wrong database')
                    return
                }
            })
        }
    }

    /**
     * List to children iframes fro analytics events.
     * Send to my socket or parent
     * @param {*} event
     */

    static _onChildIframeAnalyticsEvent(event) {
        //if YouVisit event
        if (event.data && typeof event.data === 'string') {
            const messageParts = event.data.split(':')
            const eventNamespace = messageParts[0]
            if ('youvisit' === eventNamespace) {
                const eventName = messageParts[1]
                //safely parse JSON object
                const eventData = event.data.substring(event.data.indexOf(':', eventNamespace.length + 1) + 1)
                if ('trackAnalytics' === eventName) {
                    //send data to our socket (or proxy)
                    Analytics.analyticsData.socket.send(eventData)
                }
            }
        }
    }

    static load({ userkey, sessionId, module, analyticsUrl, analyticsTime, stops, location, socket, platform }) {
        Analytics.analyticsData = Object.assign(Analytics.analyticsData, {
            platform,
            userkey,
            sessionId,
            module,
            socketUrl: analyticsUrl,
            analyticsTime,
            socket,
        })

        // create gps collector if defined (ie: if we're on mobile)
        if (typeof GpsCollector !== 'undefined' && stops && location) {
            // make sure geolocation is supported
            if (GpsCollector.isSupported()) {
                Analytics.analyticsData.gpsCollector = new GpsCollector({
                    stops,
                    location,
                })
            }
        }

        setTimeout(Analytics.establishLink.bind(Analytics), 1000)
    }

    static establishLink() {
        const { socketUrl, socket } = Analytics.analyticsData

        const analyticsSocket = (Analytics.analyticsData.socket =
            socket || new Socket(socketUrl, [], { automaticOpen: false }))

        analyticsSocket.open()

        analyticsSocket.onmessage = event => {
            const { action } = JSON.parse(event.data)

            switch (action) {
                case 'analyticsInitialized':
                    Analytics.analyticsInitialized()
                    break
                default:
            }
        }

        Analytics._startGpsListener(Analytics.analyticsData.socket)

        Analytics.initAnalytics()

        return analyticsSocket
    }

    static analyticsInitialized() {
        Analytics.analyticsData.hasInitialized = true

        // If they're queuedStops process them
        if (Analytics.analyticsData.queuedStop !== null) {
            Analytics.stopEntered(Analytics.analyticsData.queuedStop)
            Analytics.analyticsData.queuedStop = null
        }
    }

    static getAnalyticsModule(inst_id, loc_id) {
        return ajax.get(`/getAnalyticsAndRegistrationAttributes/${inst_id}/${loc_id}${window.location.search}`)
    }

    static sendAnalytics(action) {
        const { userkey, sessionId, module, inst_id, loc_id } = Analytics.analyticsData

        if (!(userkey && sessionId && module)) return

        Cookie.set('cta_button', action, 30, 'youvisit.com')

        const estid = /estid=(\w+)/g.exec(window.location.search)
        const params = estid && estid.length == 2 ? 'estid=' + estid[1] : ''

        let url = `${getEnv('REACT_APP_TRACKING_PIXEL_URL')}/cta/${inst_id}/${loc_id}.png?${params}`
        document.body.insertAdjacentHTML(
            'beforeend',
            '<img alt="youvisit action pixel" src="' + url + '" style="display:none;" />'
        )

        const queryStr = qs.parse(window.location.search, { ignoreQueryPrefix: true })

        return ajax.get('/tour/Tour/registerAction', {
            ...queryStr,
            userkey,
            sessionid: sessionId,
            module,
            action,
        })
    }

    static stopEntered(stopid) {
        const { socket, sessionId, hasInitialized } = Analytics.analyticsData

        // Socket is not ready yet so add it stop to queue
        if (socket == null || !hasInitialized) {
            Analytics.analyticsData.queuedStop = stopid
        } else {
            socket.sendMessage('stopEntered', _btoa(JSON.stringify({ stopid, sessionId })))
        }
    }

    static sessionIdChanged(sessionId) {
        Analytics.analyticsData.sessionId = sessionId
    }

    /**
     * Checks if GPS is being collected and the user is on campus, to be submitted with forms
     *
     * @return {Boolean} Is the user on campus?
     */
    static isGpsInBounds() {
        if (Analytics.gpsCollector) {
            let gpsObject = Analytics.gpsCollector.getCurrentGpsObject()

            if (gpsObject) {
                return Analytics.gpsCollector.isGpsInBounds(gpsObject)
            }
        }
        return false
    }

    static closeSocket() {
        const { socket } = Analytics.analyticsData

        if (socket) socket.close()
    }

    /**
     * Starts listening to gps changes by adding a listener to gps collector (if available).
     * Uploads gps to analytics socket when received gps is greater than distance threshold.
     *
     * @param  {Object} socket Analytics socket.
     */
    static _startGpsListener(socket) {
        const { gpsCollector } = Analytics.analyticsData

        // early exit - no gps collector (is not supported)
        if (!gpsCollector) {
            return
        }

        let prevUploadedGps = null

        // listen for gps events
        gpsCollector.addListener(gpsObject => {
            const { sessionId, hasInitialized } = Analytics.analyticsData

            // check distance against previously uploaded gps point
            if ((!prevUploadedGps || gpsCollector.distance(prevUploadedGps, gpsObject) > 10) && hasInitialized) {
                prevUploadedGps = gpsObject

                // upload gps over socket - "uploadGps" expects an array
                socket.sendMessage('uploadGps', _btoa(JSON.stringify({ points: [gpsObject], sessionId })))
                // console.log('GPS OBJECT SENT:', gpsObject)
            }
        })

        // start gps watch
        gpsCollector.startWatch()
    }

    /**
     * StopEntered wrapper functions bound to YouVisit stopChanged event to
     * ensure that bind only happens once.
     */
    static _stopEntered(event, data) {
        Analytics.stopEntered(data.stopid)
    }

    static _sessionIdChanged(event, data) {
        Analytics.sessionIdChanged(data.sessionId)
    }

    static initAnalytics(isReinitalization = false) {
        const {
            userkey,
            sessionId,
            analyticsTime,
            queuedStop,
            module,
            createAnalyticsModuleParams,
            inst_id,
            loc_id,
            platform,
        } = Analytics.analyticsData

        const isReversedOrientation =
            window.orientation !== undefined && (window.orientation === -90 || window.orientation === 90)

        const width = isReversedOrientation ? window.screen.width : window.screen.height
        const height = isReversedOrientation ? window.screen.height : window.screen.width
        let currentURL

        // Use try catch to prevent smartscript from halting in localhost
        try {
            if (window.parent && window.parent.location.href && window.location.href != window.parent.location.href)
                currentURL = window.parent && window.parent.location.href
            else currentURL = document.referrer || window.location.href
        } catch (e) {
            currentURL = document.referrer || window.location.href
        }

        // If there is a stop in queue then send a message to node server and clear the queue
        if (queuedStop !== null) {
            Analytics.analyticsData.queuedStop = null
        }

        // Forward query string parameters to backend when creating analyics
        const queryStr = qs.parse(window.location.search, { ignoreQueryPrefix: true })

        const initAnalytics = {
            endpoints: {
                ANALYTICS: process.env.REACT_APP_BASE_URL + '/functions.php',
                GPS: process.env.REACT_APP_BASE_URL + '/tour/analytics/gpsBatchUpload',
                NEWSESSION:
                    process.env.REACT_APP_BASE_URL +
                    `/getAnalyticsAndRegistrationAttributes/${inst_id}` +
                    (loc_id ? `/${loc_id}` : '') +
                    `?pl=${platform}`,
            },
            queuedStop,
            closeAnalyticsModule: {
                method: 'closeAnalyticsModule',
                userkey,
                session: sessionId,
                time: analyticsTime,
            },
            createAnalyticsModule: {
                ...queryStr,
                method: 'createAnalyticsModule',
                userkey,
                session: sessionId,
                module,
                time: analyticsTime,
                screenWidth: width,
                screenHeight: height,
                referrer: escape(document.referrer),
                currentURL,
                ...createAnalyticsModuleParams,
            },
            isReinitalization,
        }
        Analytics.analyticsData.socket.sendMessage('initAnalytics', _btoa(JSON.stringify(initAnalytics)))
    }
}

export default Analytics
